<template>
    <div :style="{ marginBottom: '20px', backgroundColor: data.backgroundColor || '#ffffff', border: data.hasRedLine ? '2px solid red' : `${data.borderWidth || 0}px solid ${data.borderColor || '#000000'}`, borderRadius: '8px' }">
        <div v-if="data.image">
            <img :src="data.image" :alt="data.alt || 'Afbeelding'" :style="{ width: '100%', height: 'auto', maxHeight: '400px', borderRadius: '8px', display: 'block' }" />
        </div>
        <div v-else :style="{ width: '100%', minHeight: '200px', backgroundColor: '#f3f4f6', borderRadius: '8px', color: '#9ca3af', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }">
            <div :style="{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', position: 'relative', zIndex: 1 }">
                <img :src="getPlaceholderImage()" alt="Placeholder" :style="{ width: '80px', height: '80px', opacity: '0.5', display: 'block' }" />
                <p :style="{ margin: 0, fontSize: '14px', color: '#9ca3af' }">Afbeelding placeholder</p>
                <input type="file" accept="image/*" @change="handleImageUpload" class="hidden" :id="fileInputId" ref="fileInput" />
                <label :for="fileInputId" :style="{ display: 'inline-block', padding: '8px 16px', backgroundColor: '#3b82f6', color: '#ffffff', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginTop: '4px' }" @mouseover="$event.target.style.backgroundColor = '#2563eb'" @mouseleave="$event.target.style.backgroundColor = '#3b82f6'">Selecteer afbeelding</label>
            </div>
            <div class="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                 :style="{ zIndex: 0 }"
                 @dragover.prevent
                 @drop="handleImageDrop"
                 @click="triggerFileInput">
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ImageBlock',
    props: {
        data: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            fileInputId: 'image-upload-' + Math.random().toString(36).substr(2, 9)
        }
    },
    methods: {
        getPlaceholderImage() {
            const svg = '<svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="10" y="15" width="60" height="50" rx="4" stroke="#9ca3af" stroke-width="2" fill="none"/><circle cx="25" cy="30" r="5" fill="#9ca3af"/><path d="M10 50 L30 35 L45 50 L60 40 L70 50 L70 60 L10 60 Z" fill="#9ca3af" opacity="0.3"/><rect x="55" y="20" width="15" height="15" rx="2" stroke="#9ca3af" stroke-width="1.5" fill="none"/></svg>';
            return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
        },
        handleImageUpload(event) {
            const file = event.target.files[0];
            if (file) {
                console.log('File selected:', file.name, file.size);
                this.processImageFile(file);
            } else {
                console.log('No file selected');
            }
            event.target.value = '';
        },

        handleImageDrop(event) {
            event.preventDefault();
            if (this.data.image) {
                alert('Deze afbeelding blok heeft al een afbeelding. Klik op "Afbeelding vervangen" om te wijzigen.');
                return;
            }
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];
                if (file.type.startsWith('image/')) {
                    this.processImageFile(file);
                } else {
                    alert('Alleen afbeeldingsbestanden zijn toegestaan.');
                }
            }
        },

        processImageFile(file) {
            console.log('Processing image file:', file.name, file.size);
            if (file.size > 2 * 1024 * 1024) {
                alert('Afbeelding is te groot. Maximum grootte is 2MB.');
                return;
            }

            const formData = new FormData();
            formData.append('image', file);
            
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || this.$page.props.csrf_token;
            
            fetch('/templates/upload-image', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRF-TOKEN': csrfToken
                }
            })
            .then(response => {
                console.log('Response status:', response.status);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    return response.json();
                } else {
                    return response.text().then(text => {
                        console.log('Non-JSON response:', text);
                        throw new Error('Server returned non-JSON response');
                    });
                }
            })
            .then(data => {
                console.log('Upload success:', data);
                if (data.imageUrl) {
                    this.$emit('update', { ...this.data, image: data.imageUrl, imagePath: data.imagePath });
                } else {
                    alert('Fout bij uploaden van afbeelding.');
                }
            })
            .catch(error => {
                console.error('Upload error:', error);
                alert('Fout bij uploaden van afbeelding.');
            });
        },

        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        generateHTML() {
            const bgColor = this.data.backgroundColor || '#ffffff';
            const borderColor = this.data.borderColor || '#000000';
            const borderWidth = this.data.borderWidth || 0;
            
            const styleAttr = this.data.hasRedLine
                ? `@if($show_clues) style="background-color: ${bgColor}; margin-bottom: 20px; border: solid red 2px;" @else style="background-color: ${bgColor}; margin-bottom: 20px; border: ${borderWidth}px solid ${borderColor};" @endif`
                : `style="background-color: ${bgColor}; margin-bottom: 20px; border: ${borderWidth}px solid ${borderColor};"`;
            
            if (this.data.image) {
                return `
                    <table width="100%" border="0" cellspacing="0" cellpadding="0" ${styleAttr} bgcolor="${bgColor}">
                        <tr>
                            <td align="center" style="padding: 0;">
                                <img src="${this.data.image}" alt="${this.data.alt || 'Afbeelding'}" width="100%" style="height: auto; display: block; border: 0;" />
                            </td>
                        </tr>
                    </table>
                `;
            } else {
                const svg = '<svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="10" y="15" width="60" height="50" rx="4" stroke="#9ca3af" stroke-width="2" fill="none"/><circle cx="25" cy="30" r="5" fill="#9ca3af"/><path d="M10 50 L30 35 L45 50 L60 40 L70 50 L70 60 L10 60 Z" fill="#9ca3af" opacity="0.3"/><rect x="55" y="20" width="15" height="15" rx="2" stroke="#9ca3af" stroke-width="1.5" fill="none"/></svg>';
                const placeholderSvg = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
                
                return `
                    <table width="100%" border="0" cellspacing="0" cellpadding="0" ${styleAttr} bgcolor="${bgColor}">
                        <tr>
                            <td width="100%" height="200" align="center" valign="middle" bgcolor="#f3f4f6" style="height: 200px; background-color: #f3f4f6; text-align: center; vertical-align: middle; color: #9ca3af; font-family: Arial, sans-serif; padding: 20px;">
                                <table width="100%" border="0" cellspacing="0" cellpadding="0">
                                    <tr>
                                        <td align="center" style="padding-bottom: 12px;">
                                            <img src="${placeholderSvg}" alt="Placeholder" width="80" height="80" style="height: 80px; opacity: 0.5; display: block; border: 0; margin: 0 auto;" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td align="center" style="font-family: Arial, sans-serif; font-size: 14px; color: #9ca3af; padding-bottom: 8px;">
                                            Afbeelding placeholder
                                        </td>
                                    </tr>
                                    <tr>
                                        <td align="center" style="padding-top: 4px;">
                                            <table border="0" cellspacing="0" cellpadding="0" align="center">
                                                <tr>
                                                    <td bgcolor="#3b82f6" style="background-color: #3b82f6; padding: 8px 16px; border-radius: 6px;">
                                                        <span style="font-family: Arial, sans-serif; font-size: 14px; font-weight: 500; color: #ffffff; text-decoration: none; display: inline-block;">Selecteer afbeelding</span>
                                                    </td>
                                                </tr>
                                            </table>
                                        </td>
                                    </tr>
                                </table>
                            </td>
                        </tr>
                    </table>
                `;
            }
        },
        getDefaultData() {
            return {
                image: '',
                alt: 'Afbeelding',
                backgroundColor: '#ffffff',
                borderColor: '#000000',
                borderWidth: 0,
                hasRedLine: false
            };
        }
    }
}
</script>